<template>
  <app-page labelledby="document-workbench-title" full-width>
    <page-header
      title-id="document-workbench-title"
      :title="document.title"
      :description="document.typeLabel"
      actions-label="Действия с документом"
    >
      <template #status>
        <status-badge
          :label="document.lifecycle.label"
          :tone="document.lifecycle.tone"
          :icon="document.lifecycle.icon"
          data-testid="document-lifecycle"
        />
      </template>
      <template #metadata>
        <dl class="document-workbench__metadata">
          <div><dt>Номер</dt><dd>{{ document.number || '—' }}</dd></div>
          <div><dt>Дата</dt><dd>{{ document.date }}</dd></div>
          <div><dt>Направление</dt><dd>{{ directionLabel }}</dd></div>
          <div><dt>Контрагент</dt><dd>{{ document.counterparty }}</dd></div>
          <template v-if="document.type === 'act_sver'">
            <div><dt>Сальдо на конец, дебет</dt><dd><amount-cell :value="document.closingDebit" /></dd></div>
            <div><dt>Сальдо на конец, кредит</dt><dd><amount-cell :value="document.closingCredit" /></dd></div>
          </template>
          <div v-else><dt>Сумма</dt><dd><amount-cell :value="document.amount" /></dd></div>
        </dl>
      </template>
      <template v-if="$slots.actions" #actions>
        <slot name="actions" />
      </template>
    </page-header>

    <q-banner v-if="offline" rounded class="document-workbench__offline" role="status" data-testid="document-offline">
      <q-icon name="cloud_off" aria-hidden="true" />
      Нет подключения. Просмотр загруженных данных доступен, действия временно отключены.
    </q-banner>

    <p v-if="actionReason" id="document-action-reason" class="document-workbench__reason" role="status">
      {{ actionReason }}
    </p>

    <nav class="document-workbench__tabs" aria-label="Разделы документа" @keydown.capture="onTabsKeydown">
      <q-tabs
        :model-value="modelValue"
        align="left"
        dense
        no-caps
        active-color="primary"
        indicator-color="primary"
        @update:model-value="$emit('update:modelValue', $event)"
      >
        <q-tab v-for="section in sections" :key="section.key" :name="section.key" :label="section.label" />
      </q-tabs>
    </nav>

    <section class="document-workbench__section" :aria-label="activeLabel" data-testid="document-workbench-section">
      <slot :name="modelValue" />
    </section>
  </app-page>
</template>

<script>
import AmountCell from '../ui/AmountCell.vue'
import AppPage from '../ui/AppPage.vue'
import PageHeader from '../ui/PageHeader.vue'
import StatusBadge from '../ui/StatusBadge.vue'
import { directionDisplay } from '../../services/documentWorkbench'

export default {
  name: 'DocumentWorkbench',
  components: { AmountCell, AppPage, PageHeader, StatusBadge },
  emits: ['update:modelValue'],
  props: {
    document: { type: Object, required: true },
    sections: { type: Array, required: true },
    modelValue: { type: String, required: true },
    offline: { type: Boolean, default: false },
    actionReason: { type: String, default: '' }
  },
  computed: {
    directionLabel() {
      return directionDisplay(this.document.direction)
    },
    activeLabel() {
      return this.sections.find((section) => section.key === this.modelValue)?.label || 'Документ'
    }
  },
  methods: {
    onTabsKeydown(event) {
      const commands = {
        ArrowLeft: -1,
        ArrowRight: 1,
        Home: 'first',
        End: 'last'
      }
      const command = commands[event.key]
      if (command === undefined) return

      const currentIndex = this.sections.findIndex((section) => section.key === this.modelValue)
      if (currentIndex < 0) return

      let nextIndex
      if (command === 'first') nextIndex = 0
      else if (command === 'last') nextIndex = this.sections.length - 1
      else nextIndex = (currentIndex + command + this.sections.length) % this.sections.length

      event.preventDefault()
      this.$emit('update:modelValue', this.sections[nextIndex].key)
      this.$nextTick(() => {
        const tabs = event.currentTarget?.querySelectorAll('[role="tab"]')
        tabs?.[nextIndex]?.focus()
      })
    }
  }
}
</script>

<style scoped>
.document-workbench__metadata {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sid-space-2) var(--sid-space-5);
  margin: 0;
}
.document-workbench__metadata div { display: flex; min-width: 8rem; gap: var(--sid-space-1); }
.document-workbench__metadata dt { color: var(--sid-color-text-secondary); }
.document-workbench__metadata dd { min-width: 0; margin: 0; overflow-wrap: anywhere; color: var(--sid-color-text-primary); }
.document-workbench__offline { margin-bottom: var(--sid-space-4); background: var(--sid-color-warning-surface); color: var(--sid-color-text-primary); }
.document-workbench__reason { margin: 0 0 var(--sid-space-3); color: var(--sid-color-text-secondary); font-size: var(--sid-font-size-label); }
.document-workbench__tabs { max-width: 100%; overflow-x: auto; border-bottom: 1px solid var(--sid-color-line); }
.document-workbench__section { min-width: 0; padding-top: var(--sid-space-5); }
@media (max-width: 599px) {
  .document-workbench__metadata { display: grid; grid-template-columns: 1fr 1fr; gap: var(--sid-space-2); }
  .document-workbench__metadata div { display: block; min-width: 0; }
  .document-workbench__metadata dt { font-size: var(--sid-font-size-caption); }
}
</style>
